import React, { useState } from 'react';
import { Chip, TextField } from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CustomButton from '../../../../../components/buttons/CustomButton';
import styles from './ProviderFormPopup.module.css';

const PLAN_NAME_MAX = 60;
const normalize = (value) => String(value || '').trim().toLowerCase();

/**
 * Planos da operadora como chips editáveis. `plans` é [{ id?, name, active }]:
 * remover um plano já salvo (com id) só o marca inativo (o servidor inativa,
 * nunca apaga, porque carteirinhas podem apontar para ele); um plano novo
 * (sem id) é descartado. Adicionar um nome igual ao de um plano inativo o reativa.
 */
function PlanChipsEditor({ plans, disabled, onChange }) {
    const [draft, setDraft] = useState('');
    const [draftError, setDraftError] = useState('');

    const visible = plans.filter((plan) => plan.active);

    const addPlan = () => {
        const name = draft.trim();
        if (!name) return;
        if (name.length > PLAN_NAME_MAX) {
            setDraftError(`Nome do plano deve ter no máximo ${PLAN_NAME_MAX} caracteres`);
            return;
        }
        const existing = plans.find((plan) => normalize(plan.name) === normalize(name));
        if (existing && existing.active) {
            setDraftError('Este plano já está na lista');
            return;
        }
        const next = existing
            ? plans.map((plan) => (plan === existing ? { ...plan, active: true } : plan))
            : [...plans, { name, active: true }];
        onChange(next);
        setDraft('');
        setDraftError('');
    };

    const removePlan = (target) => {
        const next = target.id
            ? plans.map((plan) => (plan === target ? { ...plan, active: false } : plan))
            : plans.filter((plan) => plan !== target);
        onChange(next);
    };

    const onKeyDown = (event) => {
        if (event.key === 'Enter') {
            // Enter adiciona o plano; o formulário não é enviado
            event.preventDefault();
            addPlan();
        }
    };

    return (
        <>
            <div className={styles.planInput}>
                <TextField
                    value={draft}
                    onChange={(e) => { setDraft(e.target.value); if (draftError) setDraftError(''); }}
                    onKeyDown={onKeyDown}
                    disabled={disabled}
                    size="small"
                    fullWidth
                    placeholder="Ex.: Básico, Executivo, Premium"
                    error={!!draftError}
                    helperText={draftError || 'Opcional. Digite o nome e pressione Enter ou Adicionar.'}
                    className={styles.input}
                    inputProps={{ maxLength: PLAN_NAME_MAX, 'aria-label': 'Nome do plano' }}
                />
                <CustomButton
                    style="primary"
                    variant="outlined"
                    icon={<AddRoundedIcon />}
                    label="Adicionar"
                    onClick={addPlan}
                    enabled={!disabled && draft.trim() !== ''}
                />
            </div>
            <div className={styles.planChips}>
                {visible.length === 0 ? (
                    <span className={styles.planEmpty}>Nenhum plano. A carteirinha fica só com a operadora.</span>
                ) : visible.map((plan) => (
                    <Chip
                        key={plan.id || plan.name}
                        label={plan.name}
                        size="small"
                        onDelete={disabled ? undefined : () => removePlan(plan)}
                    />
                ))}
            </div>
        </>
    );
}

export default PlanChipsEditor;
